/* ==========================================================================
   IVIM — subscription pack sizing

   A Shopify selling plan sets how OFTEN an order repeats. It cannot set how
   MUCH ships each time — that is the line quantity. So without this file a
   90-day plan bills every 90 days and ships 30 days of product.

   Every Ivim container is a 30-day supply, so:

       packs = ceil(interval days / 30)   ->   30:1  45:2  60:2  90:3

   The 45-day plan is deliberately 2 packs: it exists for people who take more
   than one serving a day, so they get through two containers in 45 days.

   Skio renders its picker into an OPEN shadow root with no ::part hooks, so
   CSS cannot reach its price but JS can. We do two things there:
     - label each plan with the pack count it actually ships
     - multiply the displayed price by that pack count
   Skio keeps owning selection and variant sync; we only correct what it shows.
   Prices are always recomputed from a remembered original, never from the
   number currently on screen, so repeated re-renders cannot compound.

   Loaded globally from theme.liquid, so it covers every product template
   (ivim-main-product, the classic main-product, and anything added later)
   rather than one section.
   ========================================================================== */
(function () {
  'use strict';

  var DAYS_PER_PACK = 30;
  var ORIGINAL = new WeakMap();   /* <option> -> its un-labelled text */
  var LABELLED = new WeakMap();   /* group -> pack count already written */
  var PRICE = new WeakMap();      /* text node -> { base, written } */

  function daysFromText(text) {
    var m = String(text || '').match(/(\d+)\s*days?/i);
    return m ? parseInt(m[1], 10) : 0;
  }

  function packsForDays(days) {
    if (!days || !DAYS_PER_PACK) return 1;
    return Math.max(1, Math.ceil(days / DAYS_PER_PACK));
  }

  /* Multiply every money figure in a string, preserving its formatting. */
  function multiplyMoney(text, factor) {
    return String(text).replace(/(\d[\d,]*)(\.(\d{2}))?/g, function (whole, intPart, _d, cents) {
      var value = parseFloat(intPart.replace(/,/g, '') + (cents ? '.' + cents : ''));
      if (isNaN(value)) return whole;
      var scaled = value * factor;
      var out = cents ? scaled.toFixed(2) : String(Math.round(scaled));
      var parts = out.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  }

  function pickers() {
    return Array.prototype.slice.call(document.querySelectorAll('skio-plan-picker'));
  }

  /* Skio's actual shape, read off a live PDP rather than assumed: two
     .group-container rows ("One time purchase" and "Subscribe"), and the four
     intervals are a <select class="selling-plan-dropdown"> INSIDE the
     Subscribe row whose option values are the real selling-plan ids. So there
     is one price for the subscribe group, not one per interval, and it already
     reflects whichever interval is chosen. */
  function intervalSelect(shadow) {
    return shadow.querySelector('select.selling-plan-dropdown') || shadow.querySelector('select');
  }

  function subscribeGroup(shadow) {
    var groups = shadow.querySelectorAll('.group-container');
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('select')) return groups[i];
    }
    return null;
  }

  function optionText(select) {
    if (!select) return '';
    var opt = select.selectedOptions && select.selectedOptions[0];
    if (opt) return ORIGINAL.has(opt) ? ORIGINAL.get(opt) : opt.textContent;
    return '';
  }

  /* Walk to the text nodes so the <s> compare-at price and the live price are
     both scaled without rebuilding the element and losing Skio's markup. */
  function moneyTextNodes(el) {
    var out = [], walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), n;
    while ((n = walker.nextNode())) if (/\d/.test(n.nodeValue)) out.push(n);
    return out;
  }

  /* Skio is a lit component: it REUSES the same text node and rewrites its
     value when the interval changes, so a one-time snapshot of the "original"
     goes stale the moment the customer switches plan — caching $55.25 from the
     30-day plan and then tripling it gives $165.75 instead of $152.10.
     So the base is re-taken whenever the value on screen is not the value we
     last wrote, which is exactly the case where lit has just re-rendered. */
  function applyPrice(node, packs) {
    var state = PRICE.get(node);
    if (!state || node.nodeValue !== state.written) state = { base: node.nodeValue };
    var next = packs > 1 ? multiplyMoney(state.base, packs) : state.base;
    if (node.nodeValue !== next) node.nodeValue = next;
    state.written = next;
    PRICE.set(node, state);
  }

  function decorate(picker) {
    var shadow = picker.shadowRoot;
    if (!shadow) return;

    var select = intervalSelect(shadow);
    var group = subscribeGroup(shadow);
    if (!select || !group) return;

    /* Put the pack count in the dropdown itself, so the customer reads what
       ships at the moment they choose the interval. */
    Array.prototype.forEach.call(select.options, function (opt) {
      if (!ORIGINAL.has(opt)) ORIGINAL.set(opt, opt.textContent.trim());
      var base = ORIGINAL.get(opt);
      var packs = packsForDays(daysFromText(base));
      var next = packs > 1 ? base + ' \u00B7 ' + packs + ' packs' : base;
      if (opt.textContent !== next) opt.textContent = next;
    });

    var packs = packsForDays(daysFromText(optionText(select)));
    group.querySelectorAll('.skio-price').forEach(function (priceEl) {
      moneyTextNodes(priceEl).forEach(function (node) { applyPrice(node, packs); });
    });

    if (LABELLED.get(group) !== packs) {
      var tag = group.querySelector('[data-ivim-packs]');
      if (packs > 1) {
        if (!tag) {
          tag = document.createElement('div');
          tag.setAttribute('data-ivim-packs', '');
          tag.style.cssText = 'font-size:11px;letter-spacing:.14em;text-transform:uppercase;' +
                              'opacity:.7;margin-top:6px';
          select.parentElement.appendChild(tag);
        }
        tag.textContent = packs + ' packs per delivery';
      } else if (tag) {
        tag.remove();
      }
      LABELLED.set(group, packs);
    }
  }

  /* How many packs the currently selected plan ships. */
  function selectedPacks(scope) {
    var field = (scope || document).querySelector('[name="selling_plan"]');
    if (!field || !field.value) return 1;          /* one-time purchase */

    var picker = (scope || document).querySelector('skio-plan-picker') ||
                 document.querySelector('skio-plan-picker');
    var shadow = picker && picker.shadowRoot;
    if (!shadow) return 1;

    var select = intervalSelect(shadow);
    var days = daysFromText(optionText(select));

    /* If the dropdown is not readable, match the light-DOM plan id against the
       option values instead — they are the same ids. */
    if (!days && select) {
      Array.prototype.forEach.call(select.options, function (opt) {
        if (String(opt.value) === String(field.value)) {
          days = daysFromText(ORIGINAL.has(opt) ? ORIGINAL.get(opt) : opt.textContent);
        }
      });
    }
    return packsForDays(days);
  }

  /* --------------------------------------------------------------------
     Add to cart. The quantity field is multiplied only for the moment the
     form is read, then put back, so the stepper keeps showing what the
     customer chose rather than jumping to 3.
     -------------------------------------------------------------------- */
  function bindForm(form) {
    if (form.hasAttribute('data-ivim-packs-bound')) return;
    form.setAttribute('data-ivim-packs-bound', '');

    form.addEventListener('submit', function () {
      var packs = selectedPacks(form.closest('[id]') || document);
      if (packs < 2) return;

      var field = form.querySelector('[name="quantity"]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'quantity';
        field.value = '1';
        form.appendChild(field);
      }
      var chosen = parseInt(field.value, 10);
      if (isNaN(chosen) || chosen < 1) chosen = 1;
      field.value = String(chosen * packs);
      setTimeout(function () { field.value = String(chosen); }, 0);
    }, true);   /* capture, so the value is in place before any ajax handler reads it */
  }

  function scan() {
    pickers().forEach(decorate);
    document.querySelectorAll('form[action*="/cart/add"]').forEach(bindForm);
  }

  function watch(picker) {
    if (!picker.shadowRoot || picker.hasAttribute('data-ivim-packs-watched')) return;
    picker.setAttribute('data-ivim-packs-watched', '');
    new MutationObserver(function () { decorate(picker); })
      .observe(picker.shadowRoot, { childList: true, subtree: true, characterData: true });
  }

  function start() {
    scan();
    pickers().forEach(watch);
    /* Skio upgrades its custom element asynchronously and re-renders on
       variant change, so keep looking rather than binding once. */
    new MutationObserver(function () { scan(); pickers().forEach(watch); })
      .observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('change', scan, true);
  }

  /* The GLP-1 bundle cannot take a selling plan, so its PDP builds its own
     /cart/add.js payload from the component variants. It asks here for the
     pack count rather than deriving it a second time. */
  window.IvimPacks = { forForm: selectedPacks, forDays: packsForDays, daysPerPack: DAYS_PER_PACK };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
